import { ArrowRight, Phone, Check, Home as HomeIcon, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import WhatsAppButton from "../../components/WhatsAppButton";
import BackToTop from "../../components/BackToTop";
import { Button } from "../../components/ui/button";
import { useSEO } from "../../lib/useSEO";
import { SITE } from "../../lib/site";
import { CITIES } from "../../lib/cities";
import { POSTS } from "../../lib/blog";
import PriceHint from "../../components/PriceHint";

const RELATED_POSTS = {
  "paysagiste-creuse": ["calendrier-tonte-taille-saisonniere", "credit-impot-50-entretien-jardin"],
  "entretien-jardin-creuse": ["credit-impot-50-entretien-jardin", "calendrier-tonte-taille-saisonniere"],
  "elagage-creuse": ["taille-elagage-automne-hiver-bon-moment", "chenilles-processionnaires-danger-traitement"],
  "destruction-frelons-creuse": ["reconnaitre-nid-frelons-asiatiques-creuse", "nid-frelons-asiatiques-vide-hiver-que-faire"],
};

const PEST_CITY_LINKS = [
  { slug: "anti-nuisibles-gueret", name: "Guéret" },
  { slug: "anti-nuisibles-la-souterraine", name: "La Souterraine" },
  { slug: "anti-nuisibles-aubusson", name: "Aubusson" },
];

export default function ServicePageLayout({
  slug,
  title,
  seoTitle,
  seoDescription,
  hero,
  intro,
  bullets,
  process,
  ctaTitle,
  ctaDesc,
  badge,
  image,
  whatsappText,
}) {
  const canonical = `https://kreuse-exterieur.fr/${slug}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: title,
        serviceType: title,
        provider: {
          "@type": "LocalBusiness",
          "@id": "https://kreuse-exterieur.fr/#business",
          name: "K'REUSE EXTÉRIEUR",
          telephone: "+33780541115",
          address: {
            "@type": "PostalAddress",
            streetAddress: "27 rue des Pradelles",
            addressLocality: "La Celle-Dunoise",
            postalCode: "23800",
            addressRegion: "Creuse",
            addressCountry: "FR",
          },
        },
        areaServed: {
          "@type": "AdministrativeArea",
          name: "Creuse (23)",
        },
        description: seoDescription,
        url: canonical,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
          { "@type": "ListItem", position: 2, name: title, item: canonical },
        ],
      },
    ],
  };

  useSEO({
    title: seoTitle,
    description: seoDescription,
    canonical,
    jsonLd,
  });

  const cityLinks = slug === "destruction-frelons-creuse"
    ? PEST_CITY_LINKS
    : Object.values(CITIES).map((c) => ({ slug: c.slug, name: c.name }));
  const relatedPosts = (RELATED_POSTS[slug] || [])
    .map((s) => POSTS.find((p) => p.slug === s))
    .filter(Boolean);

  const waHref = `https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(whatsappText || `Bonjour, je souhaite un devis pour ${title}.`)}`;

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
            <div className="absolute top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#E8F5E9] opacity-70 blur-3xl" />
          </div>
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-7">
                {/* Breadcrumb */}
                <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6">
                  <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                    <HomeIcon className="w-3.5 h-3.5" />
                    Accueil
                  </Link>
                  <ChevronRight className="w-3.5 h-3.5 mx-2" />
                  <span className="text-[#1B5E20] font-medium truncate">{title}</span>
                </nav>
                {badge && (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5EBE5] shadow-sm mb-6">
                    <span className="text-sm font-medium text-[#1B5E20]">{badge}</span>
                  </div>
                )}
                <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#1A2E1C] leading-[1.05]">
                  {title}
                </h1>
                <p className="mt-6 text-lg text-[#5C705E] leading-relaxed">
                  {hero}
                </p>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild className="bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full px-6 py-6 group">
                    <a href="/#contact">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="bg-white border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-6 py-6">
                    <a href={`tel:${SITE.phoneRaw}`}>
                      <Phone className="mr-2 w-4 h-4" />
                      {SITE.phone}
                    </a>
                  </Button>
                </div>
                <PriceHint slug={slug} />
              </div>
              <div className="lg:col-span-5">
                <div className="relative aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-2xl">
                  <img
                    src={image}
                    alt={`${title} — Creuse (23)`}
                    width="800"
                    height="1000"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1B5E20]/30 via-transparent to-transparent" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Intro + bullets */}
        <section className="py-20 bg-white/60 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="grid md:grid-cols-2 gap-10 items-start">
              <div>
                <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight text-[#1A2E1C]">
                  Notre <span className="font-medium text-[#1B5E20]">approche</span>
                </h2>
                <p className="mt-5 text-[#5C705E] leading-relaxed text-lg">{intro}</p>
              </div>
              <ul className="space-y-3">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 p-4 rounded-2xl bg-[#F1F8F2] border border-[#E5EBE5]">
                    <Check className="w-5 h-5 text-[#1B5E20] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1A2E1C]">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Process */}
        {process && process.length > 0 && (
          <section className="py-20">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight text-[#1A2E1C] mb-10 text-center">
                Comment <span className="font-medium text-[#1B5E20]">ça se passe</span>
              </h2>
              <div className="grid md:grid-cols-3 gap-5">
                {process.map((p, i) => (
                  <div key={p.title} className="relative bg-white rounded-3xl p-7 border border-[#E5EBE5]">
                    <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[#1B5E20] text-white flex items-center justify-center font-['Outfit'] font-bold shadow-lg">
                      {i + 1}
                    </div>
                    <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] mb-2">{p.title}</h3>
                    <p className="text-sm text-[#5C705E]">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Cross-links to other services */}
        <section className="py-16 bg-white/40">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div className="text-center mb-8">
              <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-3">
                Nos autres prestations
              </div>
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2E1C]">
                Découvrez nos <span className="font-medium text-[#1B5E20]">autres services</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { slug: "paysagiste-creuse", label: "Paysagiste en Creuse" },
                { slug: "entretien-jardin-creuse", label: "Entretien jardin (crédit d'impôt 50%)" },
                { slug: "elagage-creuse", label: "Élagage / abattage" },
                { slug: "destruction-frelons-creuse", label: "Destruction nids frelons" },
              ]
                .filter((s) => s.slug !== slug)
                .map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    className="flex items-center justify-between gap-3 p-4 rounded-2xl bg-white border border-[#E5EBE5] hover:border-[#1B5E20] hover:shadow-md transition-all group"
                  >
                    <span className="text-sm font-medium text-[#1A2E1C]">{s.label}</span>
                    <ArrowRight className="w-4 h-4 text-[#1B5E20] group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <ServiceCityLinks cityLinks={cityLinks} />
        <ServiceRelatedPosts posts={relatedPosts} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-[#1B5E20] to-[#2e7d32] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight">
              {ctaTitle}
            </h2>
            <p className="mt-4 text-white/85 text-lg">{ctaDesc}</p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button asChild className="bg-white text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-6 py-6 font-medium">
                <a href={`tel:${SITE.phoneRaw}`}>
                  <Phone className="mr-2 w-4 h-4" />
                  {SITE.phone}
                </a>
              </Button>
              <Button asChild className="bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full px-6 py-6 font-medium">
                <a href={waHref} target="_blank" rel="noopener noreferrer">
                  WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}

function ServiceCityLinks({ cityLinks }) {
  return (
    <section className="py-14 bg-[#F8FBF8]" data-testid="service-city-links">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-light text-[#1A2E1C] mb-6">
          Nous intervenons aussi à <span className="font-medium text-[#1B5E20]">proximité</span>
        </h2>
        <div className="flex flex-wrap justify-center gap-2">
          {cityLinks.map((c) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`service-city-link-${c.slug}`}
              className="inline-block bg-white border border-[#E5EBE5] text-[#1B5E20] text-sm px-4 py-2 rounded-full hover:bg-[#1B5E20] hover:text-white transition-colors"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceRelatedPosts({ posts }) {
  if (posts.length === 0) return null;
  return (
    <section className="py-10 md:py-14 bg-white border-t border-[#E5EBE5]" data-testid="service-related-posts">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <div className="text-xs tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-4">À lire aussi</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {posts.map((a) => (
            <Link
              key={a.slug}
              to={`/conseils/${a.slug}`}
              data-testid={`service-related-${a.slug}`}
              className="group block p-5 bg-[#F8FBF8] rounded-2xl border border-[#E5EBE5] hover:border-[#1B5E20] hover:shadow-md transition-all text-left"
            >
              <div className="text-xs text-[#5C705E] mb-1">Conseil</div>
              <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] group-hover:text-[#1B5E20] transition-colors">
                {a.title}
              </h3>
              <div className="mt-2 text-sm text-[#1B5E20] inline-flex items-center gap-1">
                Lire l'article <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
